import * as Sentry from "@sentry/react";
import {
  PARTNER_ERROR_CODES,
  PartnerAccessError,
  createPartnerSession,
  mintSedeToken,
  revokePartnerSession,
} from "./partnerApi";
import {
  getActiveSedeId,
  getPartnerSessionId,
  leaveSedeMode,
  setPartnerSessionId,
} from "./partnerStorage";

// ---------------------------------------------------------------------------
// The single source of the token the app authenticates with.
//
// Outside sede mode that is the normal JWT, exactly as before. In sede mode it
// is a short-lived DELEGATED token minted with the normal JWT. The delegated
// token lives in memory only; it is minted on demand when the cached one is
// about to expire, instead of on a background timer that browsers throttle in
// hidden tabs.
//
// HARD RULE: nothing here writes to, removes from or clears `localStorage`.
// A sede failure can at most drop sede mode (sessionStorage) — the normal
// session, shared with every other tab and with the `apps/next` zone, is never
// touched.
// ---------------------------------------------------------------------------

const {
  UNAUTHENTICATED,
  NOT_A_PARTNER,
  SEDE_NOT_IN_GROUP,
  SESSION_EXPIRED,
  FEATURE_DISABLED,
  UNAVAILABLE,
  NETWORK,
} = PARTNER_ERROR_CODES;

// A cached token is replaced once it has this little life left, so a request
// that leaves now does not reach the backend with an expired token.
const REFRESH_MARGIN_MS = 60 * 1000;

// When a refresh fails for a transient reason, the cached token is still good
// until it expires: it keeps being served down to this floor, which leaves a
// request the time to reach the backend.
const STALE_FLOOR_MS = 5 * 1000;

// After a transient failure no new mint is attempted for a while. Without it
// every request of the app would become one more call against an API that is
// down or already rate limiting (60 req/min for the whole surface).
const DEFAULT_COOLDOWN_MS = 5 * 1000;
const MAX_COOLDOWN_MS = 60 * 1000;

// `resolveEffectiveTokenAtBoot()` holds the first render. Each request has its
// own timeout, but the mint chain can be three of them in a row (mint → open
// session → mint): the boot as a whole gets this long.
const BOOT_DEADLINE_MS = 12 * 1000;

// Failures after which the sede cannot be worked any more. `SESSION_EXPIRED`
// only gets here once renewing the partner session did not help.
const PERMANENT_CODES = [
  SEDE_NOT_IN_GROUP,
  NOT_A_PARTNER,
  FEATURE_DISABLED,
  UNAUTHENTICATED,
  SESSION_EXPIRED,
];

// Expected flow outcomes, not bugs: reporting them would only be noise.
const UNREPORTED_CODES = [
  NOT_A_PARTNER,
  FEATURE_DISABLED,
  SESSION_EXPIRED,
  UNAUTHENTICATED,
  NETWORK,
];

const SENTRY_COMPONENT = "partnerAccess";
const OP_CREATE_SESSION = "createPartnerSession";
const OP_MINT_TOKEN = "mintSedeToken";

// In sede mode every request asks for the token, so a backend outage would
// repeat the same failure many times a minute.
const FAILURE_REPORT_WINDOW_MS = 5 * 60 * 1000;
const lastReportedAt = new Map();

/**
 * The sede can no longer be worked. With `currentOwner` (the default) it is
 * the sede this tab is on, and sede mode has already been left. Without it the
 * failure belongs to a mint that outlived its owner (the tab moved to another
 * sede, or another user signed in): nothing was left, and whoever is on the
 * tab now must not pay for it.
 */
export class SedeAccessLostError extends Error {
  constructor(code, { currentOwner = true } = {}) {
    super(`Sede access lost: ${code}`);
    this.name = "SedeAccessLostError";
    this.code = code;
    this.currentOwner = currentOwner;
  }
}

/** No delegated token right now. Sede mode is kept: a later call may succeed. */
export class SedeAccessUnavailableError extends Error {
  constructor(code, retryAfterSeconds) {
    super(`Sede access unavailable: ${code}`);
    this.name = "SedeAccessUnavailableError";
    this.code = code;
    if (retryAfterSeconds !== undefined) this.retryAfterSeconds = retryAfterSeconds;
  }
}

// A delegated token belongs to one sede AND to the normal session that minted
// it. Login is a SPA navigation, so another user can sign in without a reload
// while this module still holds the previous user's token: the owner check is
// what keeps it from being served to them.
//
// { token, expiresAt, companyId, normalToken } — memory only, never persisted.
let delegatedToken = null;
// { promise, companyId, normalToken } while a mint is in flight.
let pendingMint = null;
// { until, code, retryAfterSeconds, companyId, normalToken } after a transient
// mint failure. It only binds the owner whose mint failed.
let mintCooldown = null;

const delegatedTokenListeners = new Set();

const belongsTo = (entry, owner) =>
  entry !== null &&
  entry.companyId === owner.companyId &&
  entry.normalToken === owner.normalToken;

const cachedTokenFor = (owner, minRemainingMs) => {
  if (!belongsTo(delegatedToken, owner)) return null;
  return delegatedToken.expiresAt - Date.now() > minRemainingMs
    ? delegatedToken.token
    : null;
};

const forgetSedeState = () => {
  delegatedToken = null;
  mintCooldown = null;
};

// Never shorter than the default: a `Retry-After` of 0 must not turn every
// request of the app into one more mint attempt.
const cooldownMsFor = (retryAfterSeconds) =>
  Number.isFinite(retryAfterSeconds) && retryAfterSeconds >= 0
    ? Math.min(Math.max(retryAfterSeconds * 1000, DEFAULT_COOLDOWN_MS), MAX_COOLDOWN_MS)
    : DEFAULT_COOLDOWN_MS;

// A cooldown that ran out, or that belongs to another owner, is discarded.
const activeCooldownFor = (owner) => {
  if (mintCooldown === null) return null;
  if (!belongsTo(mintCooldown, owner) || Date.now() >= mintCooldown.until) {
    mintCooldown = null;
    return null;
  }
  return mintCooldown;
};

// `failure` is `{ code, retryAfterSeconds }` of the transient mint failure.
const stillValidTokenOrThrow = (owner, failure) => {
  const stillValid = cachedTokenFor(owner, STALE_FLOOR_MS);
  if (stillValid !== null) return stillValid;
  throw new SedeAccessUnavailableError(failure.code, failure.retryAfterSeconds);
};

// Anything that is not a normalized API failure (a malformed response, a bug
// in this module) is handled as an outage: it must not cost the user the sede.
const failureCode = (error) =>
  error instanceof PartnerAccessError ? error.code : UNAVAILABLE;

const reportUnexpectedFailure = (error, op) => {
  const code = failureCode(error);
  if (UNREPORTED_CODES.includes(code)) return;

  // Keyed by message, not by code: a malformed response is handled as
  // UNAVAILABLE too, and a real outage must not hide it.
  const key = `${op}:${error?.message}`;
  const now = Date.now();
  const last = lastReportedAt.get(key);
  if (last !== undefined && now - last < FAILURE_REPORT_WINDOW_MS) return;
  lastReportedAt.set(key, now);

  try {
    Sentry.captureException(error, {
      tags: { component: SENTRY_COMPONENT, op, code },
      extra: { httpStatus: error?.httpStatus },
    });
  } catch (e) {
    // Telemetry must never change the outcome of a token request.
  }
};

const reportingFailures = async (op, step) => {
  try {
    return await step();
  } catch (error) {
    reportUnexpectedFailure(error, op);
    throw error;
  }
};

const dropSedeAccess = (code) => {
  forgetSedeState();
  try {
    Sentry.addBreadcrumb({
      category: SENTRY_COMPONENT,
      message: "sede.lost",
      level: "warning",
      data: { code },
    });
  } catch (e) {
    // Same as above: losing a breadcrumb is fine, keeping sede mode is not.
  }
  leaveSedeMode();
};

// A mint can outlive its owner: the tab moved to another sede, another user
// signed in (SPA navigation) or the boot gave up on it. Its late result is
// still handed to whoever awaited it, but it must not touch the state of the
// owner that replaced it.
const isCurrentOwner = (owner) =>
  getActiveSedeId() === owner.companyId && getNormalToken() === owner.normalToken;

// Opened on behalf of `owner`. Like a minted token, a session that arrives
// after the owner was replaced must not be stored: it belongs to the previous
// user (or sede) and would be reused by the next `?sede=` handoff of this tab.
const openPartnerSession = async (owner) => {
  const sessionId = await reportingFailures(OP_CREATE_SESSION, async () => {
    const session = await createPartnerSession();
    const id = session?.sessionId;
    if (typeof id !== "string" || id === "") {
      throw new Error("Partner session response has no session id");
    }
    return id;
  });

  if (isCurrentOwner(owner)) {
    setPartnerSessionId(sessionId);
    return sessionId;
  }

  revokeQuietly(sessionId, owner.normalToken);
  // For the request that waited, the partner session it needed is not usable.
  // As a loss of a previous owner it fails that request alone: nothing is
  // dropped or cooled down for whoever is on the tab now (see mintDelegatedToken).
  throw new SedeAccessLostError(SESSION_EXPIRED, { currentOwner: false });
};

// Best effort, with the JWT that opened the session (the one in storage may
// already be another user's). Not awaited: the request that waited for the
// mint has nothing to gain from it. Left alone, the session would only hold
// one of that user's session slots until it expires.
const revokeQuietly = (sessionId, normalToken) => {
  try {
    Promise.resolve(revokePartnerSession(sessionId, { normalToken })).catch(() => {});
  } catch (e) {
    // A revoke that cannot even be sent changes nothing for the caller.
  }
};

const requestDelegatedToken = (sessionId, companyId) =>
  reportingFailures(OP_MINT_TOKEN, async () => {
    const minted = await mintSedeToken({ sessionId, companyId });
    const receivedAt = Date.now();

    // A token for any other company must never authenticate this tab.
    if (minted?.companyId !== companyId) {
      throw new Error("Sede token response belongs to another company");
    }
    const { token, expiresInSeconds } = minted;
    if (
      typeof token !== "string" ||
      token === "" ||
      !Number.isFinite(expiresInSeconds) ||
      // A lifetime within the refresh margin could never be served as fresh:
      // every call would mint again.
      expiresInSeconds * 1000 <= REFRESH_MARGIN_MS
    ) {
      throw new Error("Sede token response is malformed");
    }

    // Local clock on purpose: the server `expiresAt` would make the cache
    // depend on how far apart the two clocks are.
    return { token, expiresAt: receivedAt + expiresInSeconds * 1000 };
  });

// At most ONE partner session is opened per mint: either because none is
// stored, or because the stored one expired (12 h lifetime, or revoked).
const mintWithPartnerSession = async (owner) => {
  const { companyId } = owner;
  const storedSessionId = getPartnerSessionId();
  if (storedSessionId === null) {
    return requestDelegatedToken(await openPartnerSession(owner), companyId);
  }

  try {
    return await requestDelegatedToken(storedSessionId, companyId);
  } catch (error) {
    if (failureCode(error) !== SESSION_EXPIRED) throw error;
  }
  return requestDelegatedToken(await openPartnerSession(owner), companyId);
};

const notifyDelegatedTokenChange = (companyId) => {
  delegatedTokenListeners.forEach((listener) => {
    try {
      listener({ companyId });
    } catch (e) {
      // A listener must never break minting.
    }
  });
};

const mintDelegatedToken = async (owner) => {
  try {
    const minted = await mintWithPartnerSession(owner);
    // A token minted for an owner the tab no longer has must not be handed to
    // the request that awaited it either: it would authenticate as the previous
    // user or sede. Fail that one request as a loss of a replaced owner.
    if (!isCurrentOwner(owner)) {
      throw new SedeAccessLostError(SESSION_EXPIRED, { currentOwner: false });
    }
    delegatedToken = { ...minted, ...owner };
    mintCooldown = null;
    notifyDelegatedTokenChange(owner.companyId);
    return minted.token;
  } catch (error) {
    // Already classified by openPartnerSession() as a loss of a previous
    // owner: nothing to drop, nothing to cool down.
    if (error instanceof SedeAccessLostError) throw error;

    const code = failureCode(error);
    const stillOwner = isCurrentOwner(owner);
    if (PERMANENT_CODES.includes(code)) {
      if (stillOwner) dropSedeAccess(code);
      throw new SedeAccessLostError(code, { currentOwner: stillOwner });
    }
    // A transient failure for an owner the tab no longer has must not fall
    // back to that owner's cached token either: the awaiting request fails as
    // a loss of a replaced owner, and the current owner keeps its own state.
    if (!stillOwner) {
      throw new SedeAccessLostError(SESSION_EXPIRED, { currentOwner: false });
    }
    const failure = { code, retryAfterSeconds: error?.retryAfterSeconds };
    mintCooldown = {
      ...failure,
      ...owner,
      until: Date.now() + cooldownMsFor(failure.retryAfterSeconds),
    };
    return stillValidTokenOrThrow(owner, failure);
  }
};

// Single-flight: a page load fires many requests at once and each one asks for
// the token; they must share one mint instead of racing the rate limit.
const mintOnce = (owner) => {
  if (belongsTo(pendingMint, owner)) return pendingMint.promise;

  const promise = mintDelegatedToken(owner).finally(() => {
    if (pendingMint !== null && pendingMint.promise === promise) pendingMint = null;
  });
  pendingMint = { promise, ...owner };
  return promise;
};

export const getNormalToken = () => {
  try {
    return localStorage.getItem("token") || null;
  } catch (e) {
    return null;
  }
};

/**
 * Resolves the token to authenticate with: the normal JWT, or in sede mode a
 * delegated token with more than 60 s left (minting one if needed).
 *
 * Throws `SedeAccessLostError` when the sede can no longer be worked (sede
 * mode is left first) and `SedeAccessUnavailableError` when the token cannot
 * be obtained right now (sede mode is kept).
 *
 * A transient failure does not throw while the cached token still has more
 * than 5 s left: that token is resolved instead. It also starts a cooldown
 * (`Retry-After` kept between 5 s and 60 s, else 5 s) during which no mint is
 * attempted.
 */
export const getEffectiveToken = async () => {
  const normalToken = getNormalToken();
  const companyId = getActiveSedeId();
  if (companyId === null) {
    // Sede mode was left: a cooldown must not outlive it.
    mintCooldown = null;
    return normalToken;
  }

  if (normalToken === null) {
    // Logged out with a sede still stored: there is no session to delegate.
    forgetSedeState();
    leaveSedeMode();
    return null;
  }

  const owner = { companyId, normalToken };
  const fresh = cachedTokenFor(owner, REFRESH_MARGIN_MS);
  if (fresh !== null) return fresh;

  const cooldown = activeCooldownFor(owner);
  if (cooldown !== null) return stillValidTokenOrThrow(owner, cooldown);
  return mintOnce(owner);
};

/**
 * Sync variant for call sites that cannot await. Never mints: in sede mode it
 * returns the cached delegated token while unexpired, otherwise `null`.
 */
export const peekEffectiveToken = () => {
  const normalToken = getNormalToken();
  const companyId = getActiveSedeId();
  if (companyId === null) return normalToken;
  if (normalToken === null) return null;
  return cachedTokenFor({ companyId, normalToken }, 0);
};

/** Drops the cached delegated token so the next call mints a new one. */
export const invalidateDelegatedToken = () => {
  delegatedToken = null;
};

/**
 * Calls `listener({ companyId })` after a NEW delegated token is cached: not
 * on cache hits, and never outside sede mode. Returns the unsubscribe function.
 */
export const onDelegatedTokenChange = (listener) => {
  delegatedTokenListeners.add(listener);
  return () => {
    delegatedTokenListeners.delete(listener);
  };
};

// Rejects with a plain error, which the boot handles as an outage. The mint it
// gives up on keeps running; `isCurrentOwner` discards its late result.
const withBootDeadline = (promise) => {
  let timeoutId;
  const deadline = new Promise((resolve, reject) => {
    timeoutId = setTimeout(
      () => reject(new Error("Sede boot deadline exceeded")),
      BOOT_DEADLINE_MS
    );
  });
  return Promise.race([promise, deadline]).finally(() => clearTimeout(timeoutId));
};

/**
 * To be awaited before the first render. Never throws, and resolves within 12 s.
 *
 * The app cannot start as a sede without a delegated token, so ANY failure —
 * even a transient one, or a mint chain slower than the deadline — leaves sede
 * mode and boots the normal session, reporting why in `lostReason`.
 */
export const resolveEffectiveTokenAtBoot = async () => {
  try {
    const companyId = getActiveSedeId();
    if (companyId === null) return { mode: "normal" };

    const token = await withBootDeadline(getEffectiveToken());
    if (token !== null) return { mode: "sede", companyId };
    return { mode: "normal", lostReason: UNAUTHENTICATED };
  } catch (error) {
    const isSedeFailure =
      error instanceof SedeAccessLostError || error instanceof SedeAccessUnavailableError;
    const lostReason = isSedeFailure ? error.code : UNAVAILABLE;
    try {
      // A permanent failure already left sede mode inside getEffectiveToken().
      if (!(error instanceof SedeAccessLostError)) dropSedeAccess(lostReason);
    } catch (e) {
      // Boot must go on whatever happens here.
    }
    return { mode: "normal", lostReason };
  }
};
